import './priceDetailsModal.scss';

import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, ButtonVariant, Modal } from '@eliberty/design-system-react';

import { SELECTOR_MODE } from '~constants';
import { findModalPortal } from '~helpers';
import { AvailableOption, OrderItem, Promotion } from '~types/appsync.types';
import { Participant } from '~types/participant';

import PriceDetailsContent from './priceDetailsContent';

interface PriceDetailsModalProps {
  /** The participant list */
  participants?: Participant[];
  /** The available options */
  availableOptions?: AvailableOption[];
  /** The order items */
  orderItems?: OrderItem[];
  /** The currency used for the website */
  currency?: string;
  /** The promotion list **/
  promotions?: Promotion[];
  /** Base price calculated before applying reductions and promotions, if any */
  basePrice?: number;
  /** Final price after applying all reductions and promotions */
  finalPrice?: number;
  /** The product name **/
  productName?: string;
  /** The participant mode used to display the participant title */
  participantMode: SELECTOR_MODE;
  /** If the modal is open or not */
  isOpen?: boolean;
  /** Optional content for the trigger button */
  triggerContent?: ReactNode;
}

const PriceDetailsModal = ({
  participants,
  availableOptions,
  orderItems,
  promotions,
  currency,
  basePrice,
  finalPrice,
  productName,
  participantMode,
  isOpen,
  triggerContent,
}: PriceDetailsModalProps) => {
  const intl = useIntl();

  const [open, setOpen] = useState(isOpen ?? false);
  const handleOpen = () => setOpen(true);

  const triggerLabel = triggerContent ?? (
    <FormattedMessage id='priceDetails' defaultMessage='Price details' />
  );

  return (
    <div className='PriceDetailsModal'>
      <Button
        variant={ButtonVariant.Ghost}
        className='PriceDetailsModal_trigger'
        type='button'
        onClick={handleOpen}
      >
        <span className='PriceDetailsModal_triggerLabel'>{triggerLabel}</span>
      </Button>
      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title={intl.formatMessage({ defaultMessage: 'Price details', id: 'priceDetails' })}
        portalProps={{ target: findModalPortal() }}
        centered
      >
        <Modal.Body>
          <PriceDetailsContent
            participants={participants}
            availableOptions={availableOptions}
            orderItems={orderItems}
            promotions={promotions}
            currency={currency}
            basePrice={basePrice}
            finalPrice={finalPrice}
            productName={productName}
            participantMode={participantMode}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PriceDetailsModal;
